<template>
  <div class="row">
    <div class="col-md-12">
      <card>
        <div slot="header">
          <h4 class="card-title">Sammelbuchung bearbeiten</h4>
          <p class="card-category">Sammelbuchung Nr. {{ sammelbuchungId }}</p>
        </div>

        <div v-if="!canEdit" class="alert alert-warning">
          <strong>Bearbeitung nicht möglich:</strong> Diese Sammelbuchung kann nicht mehr bearbeitet werden.
          Die Bearbeitungszeit von {{ editTimeLimit }} Stunden ist abgelaufen.
        </div>

        <div v-if="loading" class="text-center">
          <i class="fa fa-spinner fa-spin fa-2x"></i>
          <p>Lade Sammelbuchung...</p>
        </div>

        <div v-else-if="stockRotations.length > 0">
          <form >
            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label">Grund</label>
                <multiselect
                  v-model="cause"
                  :options="causes"
                  track-by="id"
                  label="name"
                  :disabled="!canEdit"
                  placeholder="Grund auswählen" />
              </div>
              <div class="col-md-6" v-if="warehouses.length > 1">
                <label class="form-label">Lager</label>
                <multiselect
                  v-model="warehouse"
                  :options="warehouses"
                  track-by="id"
                  label="short_name"
                  :disabled="!canEdit"
                  placeholder="Lager auswählen" />
              </div>
            </div>

            <div class="row mb-3" v-if="costCenters.length">
              <div class="col-md-6">
                <label class="form-label">Kostenstelle</label>
                <multiselect
                  v-model="costCenter"
                  :options="costCenters"
                  track-by="id"
                  label="name"
                  :disabled="!canEdit"
                  placeholder="Kostenstelle auswählen" />
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Freitext</label>
              <Editor
                v-model="freeText"
                :disabled="!canEdit"
                api-key="75rbn6zzzmflhpysdjlnasy2oma7dk2f0cxs85ary6ma5ga4"
                :init="editorConfig" />
            </div>

            <div class="mb-3">
              <label class="form-label">Notiz</label>
              <input
                type="text"
                class="form-control"
                v-model="notice"
                :disabled="!canEdit" />
            </div>

            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5>Lagerbewegungen</h5>
              <button
                type="button"
                class="btn btn-success btn-sm"
                @click="addNewRotation"
                :disabled="!canEdit">
                <i class="fa fa-plus"></i> Produkt hinzufügen
              </button>
            </div>

            <div class="table-responsive" style="overflow: visible;">
              <table class="table table-striped table-bordered" style="overflow: visible;">
                <thead>
                  <tr>
                    <th>Produkt</th>
                    <th>Menge</th>
                    <th>Grund</th>
                    <th>Preis</th>
                    <th v-if="warehouses.length > 1">Lager</th>
                    <th>Batch</th>
                    <th>Seriennummer</th>
                    <th v-if="canEdit">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(rotation, index) in stockRotations" :key="rotation.id || `new-${index}`">
                    <td>
                      <div v-if="rotation.product">
                        <router-link :to="`/products/${rotation.product_id}`">
                          {{ rotation.product.name }}
                        </router-link>
                      </div>
                      <div v-else>
                        <product-select
                          :value="rotation.product"
                          @input="productSelected({ product: $event, index })"
                          :include-boxes="true"
                          :disabled="!canEdit" />
                      </div>
                    </td>
                    <td>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model.number="rotation.quantity"
                        :disabled="!canEdit"
                        min="0"
                        step="0.01" />
                    </td>
                    <td>
                      <multiselect
                        v-model="rotation.cause"
                        :options="causes"
                        track-by="id"
                        label="name"
                        :disabled="!canEdit"
                        placeholder="Grund auswählen"
                        @input="causeSelectedForRotation($event, index)" />
                    </td>
                    <td>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model.number="rotation.price"
                        :disabled="!canEdit"
                        min="0"
                        step="0.01" />
                    </td>
                    <td v-if="warehouses.length > 1">
                      <multiselect
                        v-model="rotation.warehouse"
                        :options="warehouses"
                        track-by="id"
                        label="short_name"
                        :disabled="!canEdit"
                        placeholder="Lager auswählen"
                        @input="warehouseSelectedForRotation($event, index)" />
                    </td>
                    <td>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="rotation.batch"
                        :disabled="!canEdit"
                        placeholder="Batch" />
                    </td>
                    <td>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="rotation.serial_number"
                        :disabled="!canEdit"
                        placeholder="Seriennummer" />
                    </td>
                    <td v-if="canEdit">
                      <button
                        type="button"
                        class="btn btn-danger btn-sm"
                        @click="removeRotation(index)"
                        :disabled="!canEdit">
                        <i class="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="text-right mt-3">
              <router-link to="/collective-bookings" >
              <button class="btn btn-secondary" type="button">
                Zurück
              </button>
              </router-link>
              <button
                type="submit"
                @click.prevent="submit"
                class="btn btn-primary ml-2"
                :disabled="!canEdit"
                v-prevent-double-click>
                Speichern
              </button>
            </div>
          </form>
        </div>

        <div v-else class="alert alert-info">
          Keine Lagerbewegungen gefunden.
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import ProductSelect from './product-select'
import moment from 'moment'

export default {
  components: {
    Editor,
    ProductSelect
  },
  data() {
    return {
      loading: true,
      sammelbuchungId: null,
      stockRotations: [],
      cause: null,
      warehouse: null,
      costCenter: null,
      freeText: '',
      notice: '',
      causes: [],
      warehouses: [],
      costCenters: [],
      editTimeLimit: 24, // hours - will be loaded from settings
      editorConfig: {
        height: 200,
        menubar: false,
        plugins: 'lists link',
        toolbar: 'undo redo | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | link'
      }
    }
  },
  computed: {
    canEdit() {
      if (!this.stockRotations.length) return false

      const createdAt = moment(this.stockRotations[0].created_at)
      const now = moment()
      const hoursDiff = now.diff(createdAt, 'hours')

      return hoursDiff <= this.editTimeLimit
    }
  },
  methods: {
    async loadData() {
      this.loading = true

      try {
        // Load collective booking data
        const { data: rotations } = await this.$http.get(`/group/stock_rotations/${this.sammelbuchungId}`)
        this.stockRotations = rotations.map(rotation => ({
          ...rotation,
          product: rotation.product || null,
          warehouse: rotation.warehouse || null,
          cause: rotation.cause || null
        }))

        if (rotations.length > 0) {
          this.cause = rotations[0].cause
          this.warehouse = rotations[0].warehouse
          this.costCenter = rotations[0].cost_center
          this.freeText = rotations[0].sammelbuchung_free_text || ''
          this.notice = rotations[0].notice || ''
        }

        // Load dropdown data and settings
        const [causesRes, warehousesRes, costCentersRes, settingsRes] = await Promise.all([
          this.$http.get('/causes'),
          this.$http.get('/warehouses'),
          this.$http.get('/cost_centers'),
          this.$http.get('/settings')
        ])

        this.causes = causesRes.data
        this.warehouses = warehousesRes.data
        this.costCenters = costCentersRes.data
        this.editTimeLimit = settingsRes.data.collective_booking_edit_hours || 24

      } catch (error) {
        this.$notify({
          message: 'Fehler beim Laden der Sammelbuchung',
          type: 'danger'
        })
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    async submit() {
      if (!this.canEdit) return

      // Validate that all rotations have required fields
      const invalidRotations = this.stockRotations.filter(rotation => {
        // Check if product is selected (either product_id or product object exists)
        const hasProduct = rotation.product_id || (rotation.product && rotation.product.id)
        // Check if quantity is valid (not null, not undefined, not empty string, and greater than 0)
        const hasValidQuantity = rotation.quantity !== null &&
                                rotation.quantity !== undefined &&
                                rotation.quantity !== '' &&
                                parseFloat(rotation.quantity) > 0

        return !hasProduct || !hasValidQuantity
      })

      if (invalidRotations.length > 0) {
        this.$notify({
          message: 'Bitte füllen Sie alle Pflichtfelder aus (Produkt und Menge)',
          type: 'danger'
        })
        return
      }

      try {
        await this.initCsrf()

        const updateData = {
          cause_id: this.cause ? this.cause.id : null,
          warehouse_id: this.warehouse ? this.warehouse.id : null,
          cost_center_id: this.costCenter ? this.costCenter.id : null,
          sammelbuchung_free_text: this.freeText,
          notice: this.notice,
          stock_rotations: this.stockRotations.map(rotation => ({
            id: rotation.id,
            product_id: rotation.product_id,
            quantity: rotation.quantity,
            warehouse_id: rotation.warehouse_id,
            price: rotation.price,
            batch: rotation.batch,
            serial_number: rotation.serial_number,
            cause_id: rotation.cause_id,
            cost_center_id: rotation.cost_center_id
          }))
        }

        await this.$http.put(`/group/stock_rotations/${this.sammelbuchungId}`, updateData)

        this.$notify({
          message: 'Sammelbuchung erfolgreich aktualisiert',
          type: 'success'
        })

        this.$router.push('/collective-bookings')

      } catch (error) {
        this.$notify({
          message: 'Fehler beim Speichern der Sammelbuchung',
          type: 'danger'
        })
        console.error(error)
      }
    },

    removeRotation(index) {
      if (confirm('Lagerbewegung wirklich entfernen?')) {
        this.stockRotations.splice(index, 1)
      }
    },

    addNewRotation() {
      this.stockRotations.push({
        id: null, // new rotation
        product_id: null,
        product: null,
        quantity: 1,
        cause: this.cause,
        cause_id: this.cause ? this.cause.id : null,
        warehouse: this.warehouse || (this.warehouses.length > 0 ? this.warehouses[0] : null),
        warehouse_id: this.warehouse ? this.warehouse.id : (this.warehouses.length > 0 ? this.warehouses[0].id : null),
        price: 0,
        batch: null,
        serial_number: null,
        cost_center_id: this.costCenter ? this.costCenter.id : null
      })
    },

    productSelected({ product, index }) {
      const rotation = this.stockRotations[index]
      rotation.product = product.product
      rotation.product_id = product.product ? product.product.id : null

      // Set default price based on cause direction if available
      if (product.product && rotation.cause) {
        if (rotation.cause.direction === -1) {
          rotation.price = product.product.price || 0
        } else if (rotation.cause.direction === 1) {
          rotation.price = product.product.einkaufspreis || 0
        }
      }
    },

    causeSelectedForRotation(cause, index) {
      const rotation = this.stockRotations[index]
      rotation.cause = cause
      rotation.cause_id = cause ? cause.id : null

      // Set default price based on cause direction if product is selected
      if (cause && rotation.product) {
        if (cause.direction === -1) {
          rotation.price = rotation.product.price || 0
        } else if (cause.direction === 1) {
          rotation.price = rotation.product.einkaufspreis || 0
        }
      }
    },

    warehouseSelectedForRotation(warehouse, index) {
      const rotation = this.stockRotations[index]
      rotation.warehouse = warehouse
      rotation.warehouse_id = warehouse ? warehouse.id : null
    }
  },

  created() {
    this.sammelbuchungId = this.$route.params.id
    this.loadData()
  }
}
</script>

<style scoped>
/* Fix multiselect overflow issues */
.table-responsive {
  overflow: visible !important;
}

.table {
  overflow: visible !important;
}
.btn-success {
border-radius: 16px !important;
}

/* Ensure multiselect dropdowns are visible */
.multiselect__content-wrapper {
  z-index: 9999 !important;
}

.multiselect__content {
  z-index: 9999 !important;
}

/* Fix table cell overflow for multiselects */
.table td {
  overflow: visible !important;
  position: relative;
}

/* Fix table border radius to match card */
.table {
  border-radius: 0.375rem !important;

}

.table thead th:first-child {
  border-top-left-radius: 0.375rem !important;
}

.table thead th:last-child {
  border-top-right-radius: 0.375rem !important;
}
</style>
